import React, {useMemo, useState} from 'react'
import styled from 'styled-components'
import {Breakpoints} from '@volkswagen-onehub/components-core'
import containerBottomStage from '../../../assets/css-vars/containerBottomStage'
import {boxedContainer, gridUtils} from '../../../assets/css-vars/gridUtils'
import {useSelector} from 'react-redux'
import {RootState} from '../../rootReducer'
import SingleModelInterface, {
    Promotion,
} from '../../../types/singleModelInterface'
import Brands from '../../../utils/brand/brand'
import AccordionItem from '../ui/AccordionItem'
import isEmpty from 'lodash.isempty'
import {getShowroomOpenPromotionId} from '../../../utils/promos/getShowroomOpenPromotionId'

const SinglePromotionWrapper = styled.article`
    --single-promotion-wrapper-spacing-top: 30px;
    --single-promotion-wrapper-spacing: 0;
    ${containerBottomStage}

    padding-right: var(--single-promotion-wrapper-spacing);
    padding-left: var(--single-promotion-wrapper-spacing);
    padding-top: var(--single-promotion-wrapper-spacing-top);

    @media (min-width: ${Breakpoints.b560}px) {
        --single-promotion-wrapper-spacing-top: 0;
        --single-promotion-wrapper-spacing: 60px;
    }
    @media (min-width: ${Breakpoints.b960}px) {
        --single-promotion-wrapper-spacing: 70px;
    }
    @media (min-width: ${Breakpoints.b1280}px) {
        --single-promotion-wrapper-spacing: 0;
    }
`

const SinglePromotionContainer = styled.div`
    padding-right: ${gridUtils.gap};
    padding-left: ${gridUtils.gap};
    ${boxedContainer}

    &.Single-Promotion-is-standalone {
        padding: 0 !important;
        border-top: 1px solid ${(props) => props.theme.colors.border.divider};
    }
`

export interface PromotionListForModelProps {
    model: SingleModelInterface
}

export default function PromotionListForModel({
    model,
}: PromotionListForModelProps): React.JSX.Element | null {
    const isShowroom = useSelector(
        (state: RootState) => state.general.isShowroom
    )
    const brandFromState = useSelector(
        (state: RootState) => state.general.brand
    )
    const customerType = useSelector(
        (state: RootState) => state.general.customerType
    )
    const dealerCode = useSelector(
        (state: RootState) => state.dealerData.dealerCode
    )
    const dealerName = useSelector(
        (state: RootState) => state.dealerData.dealerName
    )
    const activePromotionsExists = !!model && !isEmpty(model.related_promotions)

    const promotionsToShow: Promotion[] = useMemo(() => {
        if (!model.related_promotions.length) {
            return []
        }

        if (
            brandFromState === Brands.VW ||
            (brandFromState === Brands.VIC && isShowroom)
        ) {
            return model.related_promotions
        }

        return model.related_promotions.filter(
            (p: Promotion) => p.customer_type === customerType
        )
    }, [brandFromState, isShowroom, model.related_promotions, customerType])

    // Promotion opened by the user, `null` until they open or close one
    const [userOpenPromotionId, setUserOpenPromotionId] = useState<
        number | null
    >(null)
    const openPromotionId = getShowroomOpenPromotionId(
        promotionsToShow,
        userOpenPromotionId
    )

    if (!activePromotionsExists) {
        return null
    }

    const isDealerSelected = dealerCode !== ''

    return (
        <SinglePromotionWrapper>
            <SinglePromotionContainer className="Single-Promotion-is-standalone">
                <>
                    {promotionsToShow.map((promo: Promotion) => (
                        <AccordionItem
                            {...promo}
                            key={promo.id}
                            isOpen={openPromotionId === promo.id}
                            openIndex={openPromotionId}
                            equipments={promo.related_equipments}
                            estimate_cta_url={model.estimate_cta_url}
                            modelName={model.name}
                            modelSlug={model.slug}
                            onClick={(index, isUserClick) => {
                                // The promotion open by default is computed
                                // while rendering: its automatic opening has
                                // nothing to store
                                if (isUserClick) {
                                    setUserOpenPromotionId(index)
                                }
                            }}
                            isDealerSelected={isDealerSelected}
                            dealerName={dealerName}
                            promoScope={promo.type}
                            isPromoAvailable={promo.accepted_by_dealer}
                        />
                    ))}
                </>
            </SinglePromotionContainer>
        </SinglePromotionWrapper>
    )
}
